import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import FormField from '../../components/ui/FormField';
import { Input, Select, DateInput, NumberInput } from '../../components/ui/Input';
import ErrorBanner from '../../components/ui/ErrorBanner';
import { signup } from '../../api/admin';
import { extractErrorMessage } from '../../api/client';
import { DEPARTMENTS } from '../../auth/permissions';

const EMPTY = {
  username: '',
  password: '',
  email: '',
  role: 'ADMIN',
  employeeId: '',
  attendanceId: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  gender: 'MALE',
  phoneNumber: '',
  hireDate: '',
  jobTitle: '',
  department: 'FINANCE',
  employmentType: 'FULL_TIME',
};

export default function CreateAdminModal({ open, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const mutation = useMutation({
    mutationFn: (payload) => signup(payload),
    onSuccess: () => {
      toast.success('User created');
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      setForm(EMPTY);
      setErrors({});
      onClose?.();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  function validate() {
    const e = {};
    if (!form.username.trim()) e.username = 'Required';
    if (form.password.length < 6) e.password = 'Min 6 chars';
    if (!form.email.trim()) e.email = 'Required';
    if (!form.employeeId.trim()) e.employeeId = 'Required';
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim()) e.lastName = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function submit(ev) {
    ev?.preventDefault?.();
    if (!validate()) return;
    const payload = {
      ...form,
      username: form.username.trim(),
      email: form.email.trim(),
      employeeId: form.employeeId.trim(),
      attendanceId: form.attendanceId.trim() || null,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      middleName: form.middleName.trim() || null,
      phoneNumber: form.phoneNumber.trim() || null,
      dateOfBirth: form.dateOfBirth || null,
      hireDate: form.hireDate || null,
      jobTitle: form.jobTitle.trim() || null,
    };
    mutation.mutate(payload);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create user"
      description="Register a new USER or ADMIN. Assign departments separately after creation."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} loading={mutation.isPending}>
            Create user
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-5">
        {mutation.isError && <ErrorBanner error={mutation.error} />}

        <section>
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-muted">
            Account
          </h4>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <FormField label="Username" required error={errors.username}>
              <Input
                value={form.username}
                onChange={(e) => set('username', e.target.value)}
                error={!!errors.username}
              />
            </FormField>
            <FormField label="Password" required error={errors.password}>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => set('password', e.target.value)}
                  error={!!errors.password}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink"
                  onClick={() => setShowPassword((s) => !s)}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </FormField>
            <FormField label="Email" required error={errors.email}>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                error={!!errors.email}
              />
            </FormField>
            <FormField label="Role" required>
              <Select value={form.role} onChange={(e) => set('role', e.target.value)}>
                <option value="USER">USER</option>
                <option value="ADMIN">ADMIN</option>
                <option value="SUPERADMIN">SUPERADMIN</option>
                <option value="TESTER">TESTER</option>
              </Select>
            </FormField>
          </div>
        </section>

        <section>
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-muted">
            Personal
          </h4>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <FormField label="Employee ID" required error={errors.employeeId}>
              <Input
                value={form.employeeId}
                onChange={(e) => set('employeeId', e.target.value)}
                placeholder="EMP-001"
                error={!!errors.employeeId}
              />
            </FormField>
            <FormField label="Attendance ID" hint="eSSL system mapping ID">
              <Input
                value={form.attendanceId}
                onChange={(e) => set('attendanceId', e.target.value)}
                placeholder="e.g. 101"
              />
            </FormField>
            <FormField label="Phone Number">
              <Input
                value={form.phoneNumber}
                onChange={(e) => set('phoneNumber', e.target.value)}
              />
            </FormField>
            <FormField label="First Name" required error={errors.firstName}>
              <Input
                value={form.firstName}
                onChange={(e) => set('firstName', e.target.value)}
                error={!!errors.firstName}
              />
            </FormField>
            <FormField label="Middle Name">
              <Input
                value={form.middleName}
                onChange={(e) => set('middleName', e.target.value)}
              />
            </FormField>
            <FormField label="Last Name" required error={errors.lastName}>
              <Input
                value={form.lastName}
                onChange={(e) => set('lastName', e.target.value)}
                error={!!errors.lastName}
              />
            </FormField>
            <FormField label="Date of Birth">
              <DateInput
                value={form.dateOfBirth}
                onChange={(e) => set('dateOfBirth', e.target.value)}
              />
            </FormField>
            <FormField label="Gender">
              <Select value={form.gender} onChange={(e) => set('gender', e.target.value)}>
                <option value="MALE">MALE</option>
                <option value="FEMALE">FEMALE</option>
                <option value="OTHER">OTHER</option>
              </Select>
            </FormField>
          </div>
        </section>

        <section>
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-muted">
            Job
          </h4>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <FormField label="Department">
              <Select
                value={form.department}
                onChange={(e) => set('department', e.target.value)}
              >
                {Object.values(DEPARTMENTS).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="Job Title">
              <Input
                value={form.jobTitle}
                onChange={(e) => set('jobTitle', e.target.value)}
              />
            </FormField>
            <FormField label="Hire Date">
              <DateInput
                value={form.hireDate}
                onChange={(e) => set('hireDate', e.target.value)}
              />
            </FormField>
            <FormField label="Employment Type">
              <Select
                value={form.employmentType}
                onChange={(e) => set('employmentType', e.target.value)}
              >
                <option value="FULL_TIME">FULL_TIME</option>
                <option value="PART_TIME">PART_TIME</option>
                <option value="CONTRACT">CONTRACT</option>
                <option value="INTERN">INTERN</option>
              </Select>
            </FormField>
          </div>
        </section>

        {form.role === 'ADMIN' && (
          <div className="rounded-md bg-info/5 border border-info/20 p-3 text-xs text-info">
            ADMIN role requires department access. After creation, assign departments via the
            <strong> Departments </strong> action on the user row.
          </div>
        )}
      </form>
    </Modal>
  );
}
